import * as React from "react";
import * as ReactDOM from "react-dom";
import { Route } from "react-router-dom";
import * as modelsGet from "../../../models/get";
import * as modelsPost from "../../../models/post";
import * as modelsForm from "../../../models/form";
import * as utils from "../../../utils/utils";
import * as apiUserGroups from "../../../api/userGroups";
import * as apiStatus from "../../../api/status";
import * as apiTemplates from "../../../api/templates";
import * as globals from "../../../globals";
import { Window } from "../../../components/window";
import { Loader, LoaderStyle } from "../../../components/loader";
import { Button, ButtonType } from "../../../components/button";
import { ScrollViewer, ScrollBarVisibility } from "../../../components/scrollViewer";
import { FormGroup } from "../../../components/form";
import { ComboBox, ComboBoxType, DROPDOWN_TEXT_ITEM_HEIGHT, DropDownTextItem } from "../../../components/comboBox";
import { InputMask, InputSize, InputSideButton } from "../../../components/input";
import { InputValueType } from "../../../enumerations";
import { TextBox } from "../../../components/textBox";
import { Alert, AlertType } from "../../../components/alert";
import { GroupBox } from "../../../components/groupBox";
import { CheckBox } from "../../../components/checkbox";
import { Label, LabelContainer, LabelColor } from "../../../components/label";
import { IComboBoxValue } from "../../../models/form";
import { ITemplate } from "../../../models/post";

enum Mode
{
    create,
    edit
}

interface IViewMode
{
    id: number,
    viewMode: string
}

interface IProperties
{
    /**
     * Die ID des zu öffnenden Datensatzes. (Ist leer wenn eine neuer Datensatz erstellt werden soll.)
     */
    userGroupId?: number,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt den Modus an in dem sich der Dialog befindet.
     */
    mode?: Mode,

    /**
     * Wenn im Bearbeitungsmodus, dann ist diese Eigenschaft gefüllt.
     */
    userGroup?: modelsGet.IUserGroup,

    /**
     * Enthält alle Statuseinträge.
     */
    status?: modelsGet.IStatus[],

    /**
     * Enthält alle Statuseinträge ohne die schon ausgewählten.
     */
    statusForEdit?: modelsGet.IStatus[],

    /**
     * Enthält alle Statuseinträge ohne die schon ausgewählten.
     */
    statusForDelete?: modelsGet.IStatus[],

    /**
     * Enthält alle Statuseinträge ohne die schon ausgewählten.
     */
    statusForPrint?: modelsGet.IStatus[],

    /**
     * Enthält alle Reports.
     */
    templates?: modelsGet.ITemplate[],

    /**
     * Enthält alle Kalenderansichten.
     */
    viewModes?: IViewMode[],

    /**
     * Enthält alle Kalenderansichten ohne die schon ausgewählten.
     */
    viewModesForSelection?: IViewMode[],

    /**
     * Enthält alle Eingabefelder der Termin Eingabemaske.
     */
    eventFields?: IComboBoxValue[],

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        // Allgemein
        name?: string,

        // Rechte
        canSeeAllEvents?: boolean,
        canCreateEvent?: boolean,
        canCreateEventFreely?: boolean,
        canCreateEventInThePast?: boolean,
        canCreateEventWhenLocked?: boolean,
        canCreateEventWhenUserDefinedLocked?: boolean,
        canEditEvent?: boolean,
        canEditEventLimited?: boolean,
        canEditEventStatus?: boolean,
        canEditEventStatusIds?: number[],
        canDeleteEvent?: boolean,
        canDeleteArchivedEvent?: boolean,
        canDeleteEventStatusIds?: number[],
        canCreateLock?: boolean,
        canSeeSettings?: boolean,
        canSeeSettingCalendarGroups?: boolean,
        canSeeSettingCalendars?: boolean,
        canSeeSettingCementeries?: boolean,
        canSeeSettingGraveTypes?: boolean,
        canSeeSettingLabels?: boolean,
        canSeeSettingSubjects?: boolean,
        canSeeSettingStatus?: boolean,
        canSeeSettingDisplayTypes?: boolean,
        canSeeSettingBurialMethods?: boolean,
        canSeeSettingMourningHalls?: boolean,
        canSeeSettingPersonTypes?: boolean,
        canSeeSettingMorticians?: boolean,
        canSeeSettingPastors?: boolean,
        canSeeSettingDenominations?: boolean,
        canSeeSettingUserGroups?: boolean,
        canSeeSettingUsers?: boolean,
        canSeeSettingReports?: boolean,
        canSeeSettingApi?: boolean,
        canSeeSettingNotifications?: boolean,
        canSeeSettingOthers?: boolean,
        canSeeSettingHelp?: boolean,
        forbiddenViewModes?: number[],
        canPrintStatusIds?: number[],
        eventFields?: IComboBoxValue[],
        forbiddenReports?: number[]
    },

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export default class Dialog extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;
    private _eventFields: IComboBoxValue[] = globals.getEventFields();

    constructor(props: IProperties)
    {
        super(props);

        // Modus ermitteln.
        var mode: Mode = Mode.create;
        if (props.userGroupId > 0)
        {
            mode = Mode.edit;
        }

        // Kalenderansichten Auswahl erstellen.
        var viewModes: IViewMode[] = [
            { id: 3, viewMode: "Monat" },
            { id: 2, viewMode: "Woche" },
            { id: 1, viewMode: "Arbeitswoche" },
            { id: 0, viewMode: "Tag" },
            { id: 4, viewMode: "Planungsansicht" }
        ].sort((a, b) => a.id - b.id);

        this.state = {
            isLoading: true,
            mode: mode,
            viewModes: viewModes,
            form: {},
            errors: {}
        };

        this.initAsync(props);
    }

    private async initAsync(props: IProperties)
    {
        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(props);
        }
        else if (this.state.mode === Mode.create)
        {
            await this.initCreateModeAsync(props);
        }
    }

    private async initEditModeAsync(props: IProperties)
    {
        var userGroup: modelsGet.IUserGroup = await apiUserGroups.getUserGroup(props.userGroupId);
        var status: modelsGet.IStatus[] = await apiStatus.getStatus();
        var statusForEdit: modelsGet.IStatus[] = JSON.parse(JSON.stringify(status));
        var statusForDelete: modelsGet.IStatus[] = JSON.parse(JSON.stringify(status));
        var statusForPrint: modelsGet.IStatus[] = JSON.parse(JSON.stringify(status));
        var templates: modelsGet.ITemplate[] = await apiTemplates.getTemplates();
        var viewModesForSelection: IViewMode[] = JSON.parse(JSON.stringify(this.state.viewModes));
        var eventFields: IComboBoxValue[] = (userGroup.claims.event_fields || []).select(f => ({
            selectedValue: f,
            value: f
        }));

        statusForEdit.removeAt(s => userGroup.claims.can_edit_event_status_ids && userGroup.claims.can_edit_event_status_ids.contains(s.id));
        statusForDelete.removeAt(s => userGroup.claims.can_delete_event_status_ids && userGroup.claims.can_delete_event_status_ids.contains(s.id));
        statusForPrint.removeAt(s => userGroup.claims.can_print_status_ids && userGroup.claims.can_print_status_ids.contains(s.id));
        viewModesForSelection.removeAt(v => userGroup.claims.forbidden_view_modes && userGroup.claims.forbidden_view_modes.contains(v.id));

        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.errors = {};
            state.alertMessage = null;
            state.alertType = null;
            state.userGroup = userGroup;
            state.status = status;
            state.statusForEdit = statusForEdit;
            state.statusForDelete = statusForDelete;
            state.statusForPrint = statusForPrint;
            state.templates = templates;
            state.viewModesForSelection = viewModesForSelection;
            state.eventFields = globals.getEventFields(eventFields);

            state.form.name = userGroup.name;
            state.form.canSeeAllEvents = userGroup.claims.can_see_all_events || false;
            state.form.canCreateEvent = userGroup.claims.can_create_event || false;
            state.form.canCreateEventFreely = userGroup.claims.can_create_event_freely || false;
            state.form.canCreateEventInThePast = userGroup.claims.can_create_event_in_the_past || false;
            state.form.canCreateEventWhenLocked = userGroup.claims.can_create_event_when_locked || false;
            state.form.canCreateEventWhenUserDefinedLocked = userGroup.claims.can_create_event_when_user_defined_locked || false;
            state.form.canEditEvent = userGroup.claims.can_edit_event || false;
            state.form.canEditEventLimited = userGroup.claims.can_edit_event_limited || false;
            state.form.canEditEventStatus = userGroup.claims.can_edit_event_status || false;
            state.form.canEditEventStatusIds = userGroup.claims.can_edit_event_status_ids;
            state.form.canDeleteEvent = userGroup.claims.can_delete_event || false;
            state.form.canDeleteArchivedEvent = userGroup.claims.can_delete_archived_event || false;
            state.form.canDeleteEventStatusIds = userGroup.claims.can_delete_event_status_ids;
            state.form.canCreateLock = userGroup.claims.can_create_lock || false;
            state.form.canSeeSettings = userGroup.claims.can_see_settings || false;
            state.form.canSeeSettingCalendarGroups = userGroup.claims.can_see_setting_calendargroups || false;
            state.form.canSeeSettingCalendars = userGroup.claims.can_see_setting_calendars || false;
            state.form.canSeeSettingCementeries = userGroup.claims.can_see_setting_cementeries || false;
            state.form.canSeeSettingGraveTypes = userGroup.claims.can_see_setting_gravetypes || false;
            state.form.canSeeSettingLabels = userGroup.claims.can_see_setting_labels || false;
            state.form.canSeeSettingSubjects = userGroup.claims.can_see_setting_subjects || false;
            state.form.canSeeSettingStatus = userGroup.claims.can_see_setting_status || false;
            state.form.canSeeSettingDisplayTypes = userGroup.claims.can_see_setting_displaytypes || false;
            state.form.canSeeSettingBurialMethods = userGroup.claims.can_see_setting_burialmethods || false;
            state.form.canSeeSettingMourningHalls = userGroup.claims.can_see_setting_mourninghalls || false;
            state.form.canSeeSettingPersonTypes = userGroup.claims.can_see_setting_persontypes || false;
            state.form.canSeeSettingMorticians = userGroup.claims.can_see_setting_morticians || false;
            state.form.canSeeSettingPastors = userGroup.claims.can_see_setting_pastors || false;
            state.form.canSeeSettingDenominations = userGroup.claims.can_see_setting_denominations || false;
            state.form.canSeeSettingUserGroups = userGroup.claims.can_see_setting_usergroups || false;
            state.form.canSeeSettingUsers = userGroup.claims.can_see_setting_users || false;
            state.form.canSeeSettingReports = userGroup.claims.can_see_setting_reports || false;
            state.form.canSeeSettingApi = userGroup.claims.can_see_setting_api || false;
            state.form.canSeeSettingNotifications = userGroup.claims.can_see_setting_notifications || false;
            state.form.canSeeSettingOthers = userGroup.claims.can_see_setting_others || false;
            state.form.canSeeSettingHelp = userGroup.claims.can_see_setting_help || false;
            state.form.forbiddenViewModes = userGroup.claims.forbidden_view_modes;
            state.form.canPrintStatusIds = userGroup.claims.can_print_status_ids;
            state.form.eventFields = eventFields;
            state.form.forbiddenReports = userGroup.claims.forbidden_reports || [];

            return state;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    private async initCreateModeAsync(props: IProperties)
    {
        var status: modelsGet.IStatus[] = await apiStatus.getStatus();
        var statusForEdit: modelsGet.IStatus[] = JSON.parse(JSON.stringify(status));
        var statusForDelete: modelsGet.IStatus[] = JSON.parse(JSON.stringify(status));
        var statusForPrint: modelsGet.IStatus[] = [];
        var templates: modelsGet.ITemplate[] = await apiTemplates.getTemplates();
        var viewModesForSelection: IViewMode[] = JSON.parse(JSON.stringify(this.state.viewModes));

        // Neues State Objekt erstellen.
        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;
            newState.status = status;
            newState.statusForEdit = statusForEdit;
            newState.statusForDelete = statusForDelete;
            newState.statusForPrint = statusForPrint;
            newState.templates = templates;
            newState.viewModesForSelection = viewModesForSelection;
            newState.eventFields = globals.getEventFields();

            newState.form = {};
            newState.form.canSeeAllEvents = false;
            newState.form.canCreateEvent = false;
            newState.form.canCreateEventFreely = false;
            newState.form.canCreateEventInThePast = false;
            newState.form.canCreateEventWhenLocked = false;
            newState.form.canCreateEventWhenUserDefinedLocked = false;
            newState.form.canEditEvent = false;
            newState.form.canEditEventLimited = false;
            newState.form.canEditEventStatus = false;
            newState.form.canEditEventStatusIds = [];
            newState.form.canDeleteEvent = false;
            newState.form.canDeleteArchivedEvent = false;
            newState.form.canDeleteEventStatusIds = [];
            newState.form.canCreateLock = false;
            newState.form.canSeeSettings = false;
            newState.form.canSeeSettingCalendarGroups = false;
            newState.form.canSeeSettingCalendars = false;
            newState.form.canSeeSettingCementeries = false;
            newState.form.canSeeSettingGraveTypes = false;
            newState.form.canSeeSettingLabels = false;
            newState.form.canSeeSettingSubjects = false;
            newState.form.canSeeSettingStatus = false;
            newState.form.canSeeSettingDisplayTypes = false;
            newState.form.canSeeSettingBurialMethods = false;
            newState.form.canSeeSettingMourningHalls = false;
            newState.form.canSeeSettingPersonTypes = false;
            newState.form.canSeeSettingMorticians = false;
            newState.form.canSeeSettingPastors = false;
            newState.form.canSeeSettingDenominations = false;
            newState.form.canSeeSettingUserGroups = false;
            newState.form.canSeeSettingUsers = false;
            newState.form.canSeeSettingReports = false;
            newState.form.canSeeSettingApi = false;
            newState.form.canSeeSettingNotifications = false;
            newState.form.canSeeSettingOthers = false;
            newState.form.canSeeSettingHelp = false;
            newState.form.eventFields = [];
            newState.form.forbiddenReports = [];

            newState.form.forbiddenViewModes = [];
            newState.form.canPrintStatusIds = status.select(s => s.id);

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            if (form.canSeeAllEvents !== nextState.form.canSeeAllEvents)
                this._isSaveButtonDisabled = false;

            if (form.canCreateEvent !== nextState.form.canCreateEvent)
                this._isSaveButtonDisabled = false;

            if (form.canCreateEventFreely !== nextState.form.canCreateEventFreely)
                this._isSaveButtonDisabled = false;

            if (form.canCreateEventInThePast !== nextState.form.canCreateEventInThePast)
                this._isSaveButtonDisabled = false;

            if (form.canCreateEventWhenLocked !== nextState.form.canCreateEventWhenLocked)
                this._isSaveButtonDisabled = false;

            if (form.canCreateEventWhenUserDefinedLocked !== nextState.form.canCreateEventWhenUserDefinedLocked)
                this._isSaveButtonDisabled = false;

            if (form.canEditEvent !== nextState.form.canEditEvent)
                this._isSaveButtonDisabled = false;

            if (form.canEditEventLimited !== nextState.form.canEditEventLimited)
                this._isSaveButtonDisabled = false;

            if (form.canEditEventStatus !== nextState.form.canEditEventStatus)
                this._isSaveButtonDisabled = false;

            if (form.canEditEventStatusIds !== nextState.form.canEditEventStatusIds)
                this._isSaveButtonDisabled = false;

            if (form.canDeleteEvent !== nextState.form.canDeleteEvent)
                this._isSaveButtonDisabled = false;

            if (form.canDeleteArchivedEvent !== nextState.form.canDeleteArchivedEvent)
                this._isSaveButtonDisabled = false;

            if (form.canDeleteEventStatusIds !== nextState.form.canDeleteEventStatusIds)
                this._isSaveButtonDisabled = false;

            if (form.canCreateLock !== nextState.form.canCreateLock)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettings !== nextState.form.canSeeSettings)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingCalendarGroups !== nextState.form.canSeeSettingCalendarGroups)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingCalendars !== nextState.form.canSeeSettingCalendars)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingCementeries !== nextState.form.canSeeSettingCementeries)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingGraveTypes !== nextState.form.canSeeSettingGraveTypes)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingLabels !== nextState.form.canSeeSettingLabels)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingSubjects !== nextState.form.canSeeSettingSubjects)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingStatus !== nextState.form.canSeeSettingStatus)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingDisplayTypes !== nextState.form.canSeeSettingDisplayTypes)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingBurialMethods !== nextState.form.canSeeSettingBurialMethods)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingMourningHalls !== nextState.form.canSeeSettingMourningHalls)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingPersonTypes !== nextState.form.canSeeSettingPersonTypes)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingMorticians !== nextState.form.canSeeSettingMorticians)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingPastors !== nextState.form.canSeeSettingPastors)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingDenominations !== nextState.form.canSeeSettingDenominations)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingUserGroups !== nextState.form.canSeeSettingUserGroups)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingUsers !== nextState.form.canSeeSettingUsers)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingReports !== nextState.form.canSeeSettingReports)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingApi !== nextState.form.canSeeSettingApi)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingNotifications !== nextState.form.canSeeSettingNotifications)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingOthers !== nextState.form.canSeeSettingOthers)
                this._isSaveButtonDisabled = false;

            if (form.canSeeSettingHelp !== nextState.form.canSeeSettingHelp)
                this._isSaveButtonDisabled = false;

            if (form.forbiddenViewModes !== nextState.form.forbiddenViewModes)
                this._isSaveButtonDisabled = false;

            if (form.canPrintStatusIds !== nextState.form.canPrintStatusIds)
                this._isSaveButtonDisabled = false;

            if (form.eventFields !== nextState.form.eventFields)
                this._isSaveButtonDisabled = false;

            if (form.forbiddenReports !== nextState.form.forbiddenReports)
                this._isSaveButtonDisabled = false;
        }
    }

    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Dialog trotzdem schließen?");

        if (closeDialog)
        {
            const { onClose } = this.props;
            onClose && onClose(refresh);
            return true;
        }
    }

    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { mode, userGroup, form } = this.state;

            // Neues POST Objekt erstellen.
            var newData: modelsPost.IUserGroup = {};
            newData.name = form.name;
            newData.can_see_all_events = form.canSeeAllEvents;
            newData.can_create_event = form.canCreateEvent;
            newData.can_create_event_freely = form.canCreateEvent ? form.canCreateEventFreely : false;
            newData.can_create_event_in_the_past = form.canCreateEvent ? form.canCreateEventInThePast : false;
            newData.can_create_event_when_locked = form.canCreateEvent ? form.canCreateEventWhenLocked : false;
            newData.can_create_event_when_user_defined_locked = form.canCreateEvent ? form.canCreateEventWhenUserDefinedLocked : false;
            newData.can_edit_event = form.canEditEvent;
            newData.can_edit_event_limited = form.canEditEvent ? form.canEditEventLimited : false;
            newData.can_edit_event_status = form.canEditEvent ? form.canEditEventStatus : false;
            newData.can_edit_event_status_ids = form.canEditEvent ? form.canEditEventStatusIds : [];
            newData.can_delete_event = form.canDeleteEvent;
            newData.can_delete_archived_event = form.canDeleteEvent ? form.canDeleteArchivedEvent : false;
            newData.can_delete_event_status_ids = form.canDeleteEvent ? form.canDeleteEventStatusIds : [];
            newData.can_create_lock = form.canCreateLock;
            newData.can_see_settings = form.canSeeSettings;
            newData.can_see_setting_calendargroups = form.canSeeSettings ? form.canSeeSettingCalendarGroups : false;
            newData.can_see_setting_calendars = form.canSeeSettings ? form.canSeeSettingCalendars : false;
            newData.can_see_setting_cementeries = form.canSeeSettings ? form.canSeeSettingCementeries : false;
            newData.can_see_setting_gravetypes = form.canSeeSettings ? form.canSeeSettingGraveTypes : false;
            newData.can_see_setting_labels = form.canSeeSettings ? form.canSeeSettingLabels : false;
            newData.can_see_setting_subjects = form.canSeeSettings ? form.canSeeSettingSubjects : false;
            newData.can_see_setting_status = form.canSeeSettings ? form.canSeeSettingStatus : false;
            newData.can_see_setting_displaytypes = form.canSeeSettings ? form.canSeeSettingDisplayTypes : false;
            newData.can_see_setting_burialmethods = form.canSeeSettings ? form.canSeeSettingBurialMethods : false;
            newData.can_see_setting_mourninghalls = form.canSeeSettings ? form.canSeeSettingMourningHalls : false;
            newData.can_see_setting_persontypes = form.canSeeSettings ? form.canSeeSettingPersonTypes : false;
            newData.can_see_setting_morticians = form.canSeeSettings ? form.canSeeSettingMorticians : false;
            newData.can_see_setting_pastors = form.canSeeSettings ? form.canSeeSettingPastors : false;
            newData.can_see_setting_denominations = form.canSeeSettings ? form.canSeeSettingDenominations : false;
            newData.can_see_setting_usergroups = form.canSeeSettings ? form.canSeeSettingUserGroups : false;
            newData.can_see_setting_users = form.canSeeSettings ? form.canSeeSettingUsers : false;
            newData.can_see_setting_reports = form.canSeeSettings ? form.canSeeSettingReports : false;
            newData.can_see_setting_api = form.canSeeSettings ? form.canSeeSettingApi : false;
            newData.can_see_setting_notifications = form.canSeeSettingNotifications ? form.canSeeSettingNotifications : false;
            newData.can_see_setting_others = form.canSeeSettings ? form.canSeeSettingOthers : false;
            newData.can_see_setting_help = form.canSeeSettings ? form.canSeeSettingHelp : false;
            newData.forbidden_view_modes = form.forbiddenViewModes;
            newData.can_print_status_ids = form.canPrintStatusIds;
            newData.event_fields = (form.eventFields || []).select(f => f.selectedValue);
            newData.forbidden_reports = form.forbiddenReports;

            if (mode === Mode.edit)
            {
                await apiUserGroups.put(userGroup.id, newData);
            }
            else
            {
                await apiUserGroups.post(newData);
            }

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    private onDeleteClick = async () =>
    {
        var deleteData = confirm("Wollen Sie den Datensatz wirklich löschen?");
        if (!deleteData)
            return;

        try
        {
            this.setState({
                isLoading: true
            });

            const { userGroup } = this.state;

            await apiUserGroups.remove(userGroup.id);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                        case "internal_server_error":
                            alertMessage = errors[error];
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    private onCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
        var target = e.target;
        var value = target.checked;

        this.setState((state: IState) => {
            state.form[name] = value;
            return state;
        });
    }

    private onStatusForEditChange = (selectedItem: modelsGet.IStatus) => {
        const { statusForEdit, form } = this.state;
        const newStatusForEdit = [...statusForEdit.where(s => s.id !== selectedItem.id)];
        const newStatusForEditSelected = [...form.canEditEventStatusIds, selectedItem.id];

        this.setState((state: IState) => {
            state.statusForEdit = newStatusForEdit.sort((a, b) => {
                if (a.status < b.status) { return -1; }
                if (a.status > b.status) { return 1; }
                return 0;
            });
            state.form.canEditEventStatusIds = newStatusForEditSelected.sort((a, b) => a - b);
            return state;
        });
    }

    private onStatusForEditRemove = (status: modelsGet.IStatus) => {
        const { statusForEdit, form } = this.state;
        const newStatusForEdit = [...statusForEdit, { ...status }];
        const newStatusForEditSelected = [...form.canEditEventStatusIds.where(s => s !== status.id)];

        this.setState((state: IState) => {
            state.statusForEdit = newStatusForEdit.sort((a, b) => {
                if (a.status < b.status) { return -1; }
                if (a.status > b.status) { return 1; }
                return 0;
            });
            state.form.canEditEventStatusIds = newStatusForEditSelected.sort((a, b) => a - b);
            return state;
        });
    }

    private onStatusForDeleteChange = (selectedItem: modelsGet.IStatus) => {
        const { statusForDelete, form } = this.state;
        const newStatusForDelete = [...statusForDelete.where(s => s.id !== selectedItem.id)];
        const newStatusForDeleteSelected = [...form.canDeleteEventStatusIds, selectedItem.id];

        this.setState((state: IState) => {
            state.statusForDelete = newStatusForDelete.sort((a, b) => {
                if (a.status < b.status) { return -1; }
                if (a.status > b.status) { return 1; }
                return 0;
            });
            state.form.canDeleteEventStatusIds = newStatusForDeleteSelected.sort((a, b) => a - b);
            return state;
        });
    }

    private onStatusForDeleteRemove = (status: modelsGet.IStatus) => {
        const { statusForDelete, form } = this.state;
        const newStatusForDelete = [...statusForDelete, { ...status }];
        const newStatusForDeleteSelected = [...form.canDeleteEventStatusIds.where(s => s !== status.id)];

        this.setState((state: IState) => {
            state.statusForDelete = newStatusForDelete.sort((a, b) => {
                if (a.status < b.status) { return -1; }
                if (a.status > b.status) { return 1; }
                return 0;
            });
            state.form.canDeleteEventStatusIds = newStatusForDeleteSelected.sort((a, b) => a - b);
            return state;
        });
    }

    private onViewModeChange = (selectedItem: IViewMode) => {
        const { viewModesForSelection, form } = this.state;
        const newViewModesForSelection = [...viewModesForSelection.where(s => s.id !== selectedItem.id)];
        const newViewModesSelected = [...form.forbiddenViewModes, selectedItem.id];

        this.setState((state: IState) => {
            state.viewModesForSelection = newViewModesForSelection.sort((a, b) => a.id - b.id);
            state.form.forbiddenViewModes = newViewModesSelected.sort((a, b) => a - b);
            return state;
        });
    }

    private onViewModeRemove = (viewMode: IViewMode) => {
        const { viewModesForSelection, form } = this.state;
        const newViewModesForSelection = [...viewModesForSelection, { ...viewMode }];
        const newViewModesSelected = [...form.forbiddenViewModes.where(s => s !== viewMode.id)];

        this.setState((state: IState) => {
            state.viewModesForSelection = newViewModesForSelection.sort((a, b) => a.id - b.id);
            state.form.forbiddenViewModes = newViewModesSelected.sort((a, b) => a - b);
            return state;
        });
    }

    private onStatusForPrintChange = (selectedItem: modelsGet.IStatus) =>
    {
        const { statusForPrint, form } = this.state;
        const newStatusForPrint = [...statusForPrint.where(s => s.id !== selectedItem.id)];
        const newStatusForPrintSelected = [...form.canPrintStatusIds, selectedItem.id];

        this.setState((state: IState) => {
            state.statusForPrint = newStatusForPrint.sort((a, b) => {
                if (a.status < b.status) { return -1; }
                if (a.status > b.status) { return 1; }
                return 0;
            });
            state.form.canPrintStatusIds = newStatusForPrintSelected.sort((a, b) => a - b);
            return state;
        });
    }

    private onStatusForPrintRemove = (status: modelsGet.IStatus) =>
    {
        const { statusForPrint, form } = this.state;
        const newStatusForPrint = [...statusForPrint, { ...status }];
        const newStatusForPrintSelected = [...form.canPrintStatusIds.where(s => s !== status.id)];

        this.setState((state: IState) => {
            state.statusForPrint = newStatusForPrint.sort((a, b) => {
                if (a.status < b.status) { return -1; }
                if (a.status > b.status) { return 1; }
                return 0;
            });
            state.form.canPrintStatusIds = newStatusForPrintSelected.sort((a, b) => a - b);
            return state;
        });
    }

    private onEventFieldChange = (selectedItem: IComboBoxValue) => {
        const { eventFields, form } = this.state;
        const newEventFields = [...eventFields.where(f => f.selectedValue !== selectedItem.selectedValue)];
        const newEventFieldsSelected = [...form.eventFields, { ...selectedItem }];

        this.setState((state: IState) => {
            state.eventFields = newEventFields.sort((a, b) => {
                if (a.selectedValue < b.selectedValue) { return -1; }
                if (a.selectedValue > b.selectedValue) { return 1; }
                return 0;
            });
            state.form.eventFields = newEventFieldsSelected.sort((a, b) => {
                if (a.selectedValue < b.selectedValue) { return -1; }
                if (a.selectedValue > b.selectedValue) { return 1; }
                return 0;
            });
            return state;
        });
    }

    private onEventFieldRemove = (field: IComboBoxValue) => {
        const { eventFields, form } = this.state;
        const newEventFields = [...eventFields, { ...field }];
        const newEventFieldsSelected = [...form.eventFields.where(f => f.selectedValue !== field.selectedValue)];

        this.setState((state: IState) => {
            state.eventFields = newEventFields.sort((a, b) => {
                if (a.selectedValue < b.selectedValue) { return -1; }
                if (a.selectedValue > b.selectedValue) { return 1; }
                return 0;
            });
            state.form.eventFields = newEventFieldsSelected.sort((a, b) => {
                if (a.selectedValue < b.selectedValue) { return -1; }
                if (a.selectedValue > b.selectedValue) { return 1; }
                return 0;
            });
            return state;
        });
    }

    private onTemplateChange = (selectedItem: modelsGet.ITemplate) => {
        this.setState((state: IState) => {
            state.form.forbiddenReports = [...state.form.forbiddenReports, selectedItem.id];
            return state;
        });
    }

    private onTemplateRemove = (selectedItem: modelsGet.ITemplate) => {
        this.setState((state: IState) => {
            state.form.forbiddenReports = [...state.form.forbiddenReports.where(r => r !== selectedItem.id)];
            return state;
        });
    }

    private renderContent(): JSX.Element
    {
        const {
            status,
            statusForEdit,
            statusForDelete,
            statusForPrint,
            templates,
            viewModes,
            viewModesForSelection,
            eventFields,
            form,
            errors
        } = this.state;

        const templatesToChoose = templates.where(t => !form.forbiddenReports.contains(t.id));

        return (
            <div className="settings_dialog_content">
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    style={{
                        padding: 10,
                        paddingBottom: 50
                    }}>
                    <GroupBox
                        headerText="Allgemein"
                        contentStyle={{
                            paddingTop: 10
                        }}>
                        <FormGroup>
                            <TextBox
                                label="Name"
                                value={form.name}
                                size={InputSize.normal}
                                spellCheck={true}
                                errorMessage={errors["Name"]}
                                onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "name")} />
                        </FormGroup>
                    </GroupBox>
                    <GroupBox
                        headerText="Rechte"
                        contentStyle={{
                            paddingTop: 20
                        }}>
                        <FormGroup>
                            <CheckBox
                                label="Alle Termine einsehen"
                                hint="Alle Termine dürfen eingesehen werden. Falls dieses Recht verweigert wird, können nur die Termine eingesehen werden, die der Benutzer erstellt hat."
                                isChecked={form.canSeeAllEvents}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeAllEvents")} />
                        </FormGroup>
                        <FormGroup hasBorderTop>
                            <CheckBox
                                label="Termine erstellen"
                                isChecked={form.canCreateEvent}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canCreateEvent")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Termine in der Vergangenheit erstellen"
                                hint="Termine dürfen in der Vergangenheit erstellt werden."
                                isChecked={form.canCreateEventInThePast}
                                isDisabled={!form.canCreateEvent}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canCreateEventInThePast")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Termine ohne Einschränkung erstellen"
                                hint="Wenn erlaubt, können Termine erstellt werden die sich überschneiden."
                                isChecked={form.canCreateEventFreely}
                                isDisabled={!form.canCreateEvent}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canCreateEventFreely")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Termine in gesperrten Kalender Bereichen erstellen"
                                hint="Wenn erlaubt, können Termine in gesperrten Bereichen erstellt werden. Ausgeschlossen sind hier benutzerdefinierte gesperrte Zeiten."
                                isChecked={form.canCreateEventWhenLocked}
                                isDisabled={!form.canCreateEvent}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canCreateEventWhenLocked")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Termine in gesperrten Zeiträumen erstellen"
                                hint="Wenn erlaubt, können Termine in gesperrten Zeiträumen erstellt werden. Gesperrte Zeiträume sind vom Anwender erstelle Sperrzeiten. Ausgeschlossen sind hier gesperrte Kalender Bereiche."
                                isChecked={form.canCreateEventWhenUserDefinedLocked}
                                isDisabled={!form.canCreateEvent}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canCreateEventWhenUserDefinedLocked")} />
                        </FormGroup>
                        <FormGroup hasBorderTop>
                            <CheckBox
                                label="Termine bearbeiten"
                                isChecked={form.canEditEvent}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canEditEvent")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Termine eingeschränkt bearbeiten"
                                hint="Wenn angehakt, dürfen folgende Felder nicht bearbeitet werden: Betreff, Kalender, Anzeigetyp, Startdatum, Zeit von, Zeit bis, Rüstzeit davor, Rüstzeit danach, Bestattungsart, Bestatter, Bemerkung, verstorbenen Daten"
                                isChecked={form.canEditEventLimited}
                                isDisabled={!form.canEditEvent}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canEditEventLimited")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Termin Status bearbeiten"
                                hint="Der Status eines Termins darf bearbeitet werden. Das Feld wird ausgeblendet wenn des Recht nicht erteilt wird."
                                isChecked={form.canEditEventStatus}
                                isDisabled={!form.canEditEvent}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canEditEventStatus")} />
                            <ComboBox
                                style={{
                                    marginLeft: 28
                                }}
                                canRemoveSelection={true}
                                label="Termine bearbeiten bei bestimmten Status"
                                dataSource={statusForEdit}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                onSelectionChanged={(data) => this.onStatusForEditChange(data)}
                                valueMember="id"
                                displayMember="status"
                                isDisabled={!form.canEditEvent || (!statusForEdit || statusForEdit.length === 0)}>
                                {
                                    (item: modelsGet.IStatus) =>
                                    {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.status} />
                                        );
                                    }
                                }
                            </ComboBox>
                            {
                                form.canEditEventStatusIds && form.canEditEventStatusIds.length > 0 &&
                                <LabelContainer
                                    style={{
                                        marginLeft: 28
                                    }}>
                                    {
                                        status.where(s => form.canEditEventStatusIds.contains(s.id)).map((data) =>
                                        {
                                            return (
                                                <Label
                                                    dataSource={data}
                                                    text={data.status}
                                                    isButtonVisible={true}
                                                    color={LabelColor.blue}
                                                    onButtonClick={this.onStatusForEditRemove} />
                                            );
                                        })
                                    }
                                </LabelContainer>
                            }
                        </FormGroup>
                        <FormGroup hasBorderTop>
                            <CheckBox
                                label="Termine löschen"
                                isChecked={form.canDeleteEvent}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canDeleteEvent")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Termine aus dem Archiv löschen"
                                isChecked={form.canDeleteArchivedEvent}
                                isDisabled={!form.canDeleteEvent}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canDeleteArchivedEvent")} />
                            <ComboBox
                                style={{
                                    marginLeft: 28
                                }}
                                canRemoveSelection={true}
                                label="Termine löschen bei bestimmten Status"
                                dataSource={statusForDelete}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                onSelectionChanged={(data) => this.onStatusForDeleteChange(data)}
                                valueMember="id"
                                displayMember="status"
                                isDisabled={!form.canDeleteEvent || (!statusForDelete || statusForDelete.length === 0)}>
                                {
                                    (item: modelsGet.IStatus) =>
                                    {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.status} />
                                        );
                                    }
                                }
                            </ComboBox>
                            {
                                form.canDeleteEventStatusIds && form.canDeleteEventStatusIds.length > 0 &&
                                <LabelContainer
                                    style={{
                                        marginLeft: 28
                                    }}>
                                    {
                                        status.where(s => form.canDeleteEventStatusIds.contains(s.id)).map((data) =>
                                        {
                                            return (
                                                <Label
                                                    dataSource={data}
                                                    text={data.status}
                                                    isButtonVisible={true}
                                                    color={LabelColor.blue}
                                                    onButtonClick={this.onStatusForDeleteRemove} />
                                            );
                                        })
                                    }
                                </LabelContainer>
                            }
                        </FormGroup>
                        <FormGroup hasBorderTop>
                            <CheckBox
                                label="Zeiten sperren"
                                isChecked={form.canCreateLock}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canCreateLock")} />
                        </FormGroup>
                        <FormGroup hasBorderTop>
                            <CheckBox
                                label="Einstellungen vornehmen"
                                isChecked={form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettings")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Kalendergruppen"
                                isChecked={form.canSeeSettingCalendarGroups}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingCalendarGroups")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Kalender"
                                isChecked={form.canSeeSettingCalendars}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingCalendars")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Friedhöfe"
                                isChecked={form.canSeeSettingCementeries}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingCementeries")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Grabarten"
                                isChecked={form.canSeeSettingGraveTypes}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingGraveTypes")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Labels"
                                isChecked={form.canSeeSettingLabels}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingLabels")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Betreffzeilen"
                                isChecked={form.canSeeSettingSubjects}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingSubjects")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Status"
                                isChecked={form.canSeeSettingStatus}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingStatus")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Anzeigearten"
                                isChecked={form.canSeeSettingDisplayTypes}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingDisplayTypes")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Bestattungsarten"
                                isChecked={form.canSeeSettingBurialMethods}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingBurialMethods")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Trauerhallen"
                                isChecked={form.canSeeSettingMourningHalls}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingMourningHalls")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Personenarten"
                                isChecked={form.canSeeSettingPersonTypes}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingPersonTypes")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Bestatter"
                                isChecked={form.canSeeSettingMorticians}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingMorticians")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Pfarrer"
                                isChecked={form.canSeeSettingPastors}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingPastors")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Konfession"
                                isChecked={form.canSeeSettingDenominations}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingDenominations")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Benutzergruppen"
                                isChecked={form.canSeeSettingUserGroups}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingUserGroups")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Benutzer"
                                isChecked={form.canSeeSettingUsers}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingUsers")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Reports"
                                isChecked={form.canSeeSettingReports}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingReports")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="API"
                                isChecked={form.canSeeSettingApi}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingApi")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Benachrichtigungen"
                                isChecked={form.canSeeSettingNotifications}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingNotifications")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Sonstiges"
                                isChecked={form.canSeeSettingOthers}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingOthers")} />
                            <CheckBox
                                style={{
                                    marginLeft: 28
                                }}
                                label="Hilfe"
                                isChecked={form.canSeeSettingHelp}
                                isDisabled={!form.canSeeSettings}
                                onCheckedChanged={(e) => this.onCheckboxChanged(e, "canSeeSettingHelp")} />
                        </FormGroup>
                        <FormGroup hasBorderTop>
                            <ComboBox
                                canRemoveSelection={true}
                                label="Kalenderansichten verweigern"
                                dataSource={viewModesForSelection}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                onSelectionChanged={this.onViewModeChange}
                                valueMember="id"
                                displayMember="viewMode"
                                isDisabled={!viewModesForSelection || viewModesForSelection.length === 0}>
                                {
                                    (item: IViewMode) =>
                                    {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.viewMode} />
                                        );
                                    }
                                }
                            </ComboBox>
                            {
                                form.forbiddenViewModes && form.forbiddenViewModes.length > 0 &&
                                <LabelContainer>
                                    {
                                        viewModes.where(v => form.forbiddenViewModes.contains(v.id)).map((data) =>
                                        {
                                            return (
                                                <Label
                                                    dataSource={data}
                                                    text={data.viewMode}
                                                    isButtonVisible={true}
                                                    color={LabelColor.red}
                                                    onButtonClick={this.onViewModeRemove} />
                                            );
                                        })
                                    }
                                </LabelContainer>
                            }
                        </FormGroup>
                        <FormGroup hasBorderTop>
                            <ComboBox
                                canRemoveSelection={true}
                                label="Öffnen von Reports bei folgenden Statuseinträgen erlauben"
                                dataSource={statusForPrint}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                onSelectionChanged={(data) => this.onStatusForPrintChange(data)}
                                valueMember="id"
                                displayMember="status"
                                isDisabled={!statusForPrint || statusForPrint.length === 0}>
                                {
                                    (item: modelsGet.IStatus) =>
                                    {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.status} />
                                        );
                                    }
                                }
                            </ComboBox>
                            {
                                form.canPrintStatusIds && form.canPrintStatusIds.length > 0 &&
                                <LabelContainer style={{
                                    marginBottom: 8
                                }}>
                                    {
                                        status.where(s => form.canPrintStatusIds.contains(s.id)).map((data) =>
                                        {
                                            return (
                                                <Label
                                                    dataSource={data}
                                                    text={data.status}
                                                    isButtonVisible={true}
                                                    color={LabelColor.blue}
                                                    onButtonClick={this.onStatusForPrintRemove} />
                                            );
                                        })
                                    }
                                </LabelContainer>
                            }
                            <ComboBox
                                label="Reports verweigern"
                                dataSource={templatesToChoose}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                onSelectionChanged={(data) => this.onTemplateChange(data)}
                                valueMember="id"
                                displayMember="display_name"
                                isDisabled={(!templatesToChoose || templatesToChoose.length === 0)}>
                                {
                                    (item: modelsGet.ITemplate) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.id}
                                                value={item.id}
                                                displayValue={item.display_name} />
                                        );
                                    }
                                }
                            </ComboBox>
                            {
                                <LabelContainer>
                                    {
                                        templates.where(t => form.forbiddenReports.contains(t.id)).map((data) => {
                                            return (
                                                <Label
                                                    dataSource={data}
                                                    text={data.display_name}
                                                    isButtonVisible={true}
                                                    color={LabelColor.red}
                                                    onButtonClick={this.onTemplateRemove} />
                                            );
                                        })
                                    }
                                </LabelContainer>
                            }
                        </FormGroup>
                        <FormGroup hasBorderTop>
                            <ComboBox
                                canRemoveSelection={true}
                                label="Eingabefelder in der Termineingabemaske verweigern"
                                dataSource={eventFields}
                                dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                                comboBoxType={ComboBoxType.dropDownList}
                                onSelectionChanged={(data) => this.onEventFieldChange(data)}
                                valueMember="id"
                                displayMember="status"
                                isDisabled={!eventFields || eventFields.length === 0}>
                                {
                                    (item: IComboBoxValue) => {
                                        return (
                                            <DropDownTextItem
                                                key={item.selectedValue}
                                                value={item.selectedValue}
                                                displayValue={item.value} />
                                        );
                                    }
                                }
                            </ComboBox>
                            {
                                form.eventFields && form.eventFields.length > 0 &&
                                <LabelContainer>
                                    {
                                        form.eventFields.map((data) => {
                                            let field: IComboBoxValue = this._eventFields.where(d => d.selectedValue === data.selectedValue).first();
                                            return (
                                                <Label
                                                    dataSource={field}
                                                    text={field.value}
                                                    isButtonVisible={true}
                                                    color={LabelColor.red}
                                                    onButtonClick={this.onEventFieldRemove} />
                                            );
                                        })
                                    }
                                </LabelContainer>
                            }
                        </FormGroup>
                    </GroupBox>
                </ScrollViewer>
            </div>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            isLoading,
            mode,
            userGroup,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "plus";
                title = "Neue Benutzergruppe erstellen";
                buttonHeader = "Erstellen";
            }
            else
            {
                icon = "pencil";
                title = `Benutzergruppe "${userGroup.name}" bearbeiten`;
                buttonHeader = "Speichern";
            }
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                width={660}
                height={710}
                onEnterKeyUp={this.onSaveOrCreateClick}
                onEscapeKeyUp={() => this.onClosing(false)}>
                <div className="settings_dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="settings_dialog_actions">
                        {
                            mode === Mode.edit &&
                            <Button
                                text="Löschen"
                                type={ButtonType.danger}
                                onClick={this.onDeleteClick} />
                        }
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={this._isSaveButtonDisabled}
                            onClick={this.onSaveOrCreateClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}